import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'cookies-js';
import isFunction from 'lodash/isFunction';
import get from 'lodash/get';

import lazyGettext from 'js-utils/lazy-gettext';
import Button from 'eventbrite_design_system/button/Button';

import PageHeader from '../../components/PageHeader';

import { getAction } from '@eb/destination-utils';

import {
    acknowledgeMessage as acknowledgeMessageAction,
    rejectMessage as rejectMessageAction,
} from '../../actions';

import './confirmationScreen.scss';

import LogoEBrandSvg from 'eventbrite_design_system/iconography/icons/LogoEBrand';

const REMIND_ME_CONTENT = {
    title: lazyGettext("Don't miss out!"),
    message: (eventTitle) =>
        lazyGettext(
            'Sign-in to be reminded about %(eventTitle)s and get notified 1 hour before the on-sale starts.',
            { eventTitle },
        ),
    primaryButtonText: lazyGettext('Get Started'),
    secondaryButtonText: lazyGettext('Cancel'),
    imageSize: 'large',
};

const ONBOARDING_CONTENT = {
    title: lazyGettext('Sign in to save your interests'),
    message: lazyGettext(
        'We can give you better recommendations if we know what you like.',
    ),
    primaryButtonText: lazyGettext("Let's go"),
    secondaryButtonText: lazyGettext('Cancel'),
    imageSize: 'large',
};

const FOLLOW_ORGANIZER_CONTENT = {
    title: lazyGettext('Stay in the know'),
    message: (organizerName) =>
        lazyGettext(
            'Sign-in to follow %(organizerName)s and get notified of future events by this organizer.',
            { organizerName },
        ),
    primaryButtonText: lazyGettext('Get Started'),
    secondaryButtonText: lazyGettext('Cancel'),
    imageSize: 'small',
};

const FOLLOW_COLLECTION_CONTENT = {
    title: lazyGettext('Stay in the know'),
    message: (collectionName) =>
        lazyGettext(
            'Sign-in to follow %(collectionName)s and get notified of future events in this collection.',
            { collectionName },
        ),
    primaryButtonText: lazyGettext('Get Started'),
    secondaryButtonText: lazyGettext('Cancel'),
    imageSize: 'large',
};

const FOLLOW_ORGANIZER_ACTION = 'followOrganizer';

const MESSAGE_MAP = {
    remindMe: REMIND_ME_CONTENT,
    consumerOnboarding: ONBOARDING_CONTENT,
    [FOLLOW_ORGANIZER_ACTION]: FOLLOW_ORGANIZER_CONTENT,
    followCollection: FOLLOW_COLLECTION_CONTENT,
};

const DEFAULT_EXTRA_CONTENT = {
    title: '',
    image: 'https://www.eventbrite.com/static/images/search/placeholder1.png',
    imageSize: 'large',
};
const SMALL_IMAGE_STYLES = {
    width: '100px',
};

//Map of ID to Cookie Value
const ID_TYPE_TO_COOKIE_NAME_MAP = {
    organizerId: {
        val: 'ebOrganizerId',
        action: FOLLOW_ORGANIZER_ACTION,
    },
};

const ExtraContentSection = ({ image, imageSize, title, message }) => (
    <div className="signin-confirmation__extra-content-section">
        <img
            className="signin-confirmation__image"
            src={image}
            alt=""
            style={imageSize === 'small' ? SMALL_IMAGE_STYLES : null}
        />
        <p className="signin-confirmation__title eds-text-bl">{title}</p>
        <p className="signin-confirmation__message">
            {isFunction(message) ? message(title) : message}
        </p>
    </div>
);

export class ConfirmationScreen extends React.PureComponent {
    static propTypes = {
        onContinue: PropTypes.func.isRequired,
        onCancel: PropTypes.func.isRequired,
        forward: PropTypes.string,
    };

    constructor(props) {
        super(props);

        let newState;
        const userAction = getAction();

        if (userAction) {
            newState = this._getStateFromUserAction(userAction);
        } else {
            const queryParams = get(props, ['location', 'query']);

            newState = this._getStateFromQueryParams(queryParams);
        }

        if (newState) {
            this.state = newState;
        }
    }

    state = {
        content: REMIND_ME_CONTENT,
        extraContent: DEFAULT_EXTRA_CONTENT,
        forward: this.props.forward,
    };

    _getStateFromUserAction = (userAction) => {
        const { origin, action, payload } = userAction;

        return {
            action,
            forward: origin,
            hydratedBy: 'cookie',
            extraContent: payload,
            content: MESSAGE_MAP[action],
        };
    };

    _getStateFromQueryParams = (queryParams) => {
        let state;

        if (queryParams) {
            const { action, referrer, ...extraContent } = queryParams;

            if (action && Object.keys(MESSAGE_MAP).indexOf(action) > -1) {
                state = {
                    action,
                    extraContent,
                    forward: referrer,
                    content: MESSAGE_MAP[action],
                    hydratedBy: 'queryParams',
                };
            }
        }

        return state;
    };

    _setUserActionCookieFromQueryParams = () => {
        const { extraContent, action } = this.state;

        for (const key in ID_TYPE_TO_COOKIE_NAME_MAP) {
            if (get(extraContent, [key])) {
                const {
                    action: cookieAction,
                    val,
                } = ID_TYPE_TO_COOKIE_NAME_MAP[key];

                if (cookieAction === action) {
                    Cookies.set(val, get(extraContent, [key]));
                }
            }
        }
    };

    _handleContinue = () => {
        const { hydratedBy } = this.state;

        if (hydratedBy === 'queryParams') {
            this._setUserActionCookieFromQueryParams();
        }

        this.props.onContinue();
    };

    _handleCancel = () => {
        this.props.onCancel(this.state.forward);
    };

    render() {
        const {
            title,
            message,
            primaryButtonText,
            secondaryButtonText,
            imageSize,
        } = this.state.content;
        const { image, title: extraContentTitle } = this.state.extraContent;

        const headerProps = {
            headerText: title,
            iconType: <LogoEBrandSvg />,
            iconColor: 'white',
        };
        const extraContentProps = {
            image,
            imageSize,
            message,
            title: extraContentTitle,
        };

        return (
            <section className="signin-confirmation">
                <div className="eds-g-group">
                    <div
                        className="
                            eds-g-cell
                            eds-g-cell-sm-8-12 eds-g-offset-sm-2-12
                            eds-g-cell-sw-6-12 eds-g-offset-sw-3-12
                            eds-g-cell-md-4-12 eds-g-offset-md-4-12
                            eds-g-cell--has-overflow
                        "
                    >
                        <PageHeader {...headerProps} />
                        <ExtraContentSection {...extraContentProps} />
                        <div className="signin-confirmation__actions">
                            <span className="signin-confirmation__cancel-btn">
                                <Button onClick={this._handleCancel}>
                                    {secondaryButtonText}
                                </Button>
                            </span>
                            <span className="signin-confirmation__continue-btn">
                                <Button
                                    style="fill"
                                    onClick={this._handleContinue}
                                >
                                    {primaryButtonText}
                                </Button>
                            </span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}

export const _mapStateToProps = (state) => ({
    forward: state.authentication.forward,
});

export const _mapDispatchToProps = {
    onContinue: acknowledgeMessageAction,
    onCancel: rejectMessageAction,
};

export default connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(ConfirmationScreen);
